import { AmbientField } from '../../primitives/AmbientField.tsx';
import { Text } from '../../primitives/Text.tsx';
import styles from './LoadingState.module.css';

/**
 * The page-level loading state — imported from the Cavaquinho Design System
 * canvas ("Cavaquinho Loading Animation") and translated into this library's
 * tokens: the cavaquinho lifts and settles as though about to be played,
 * its strings shimmer, the soundhole rings out, and a note drifts free on
 * each hue of the brand.
 *
 * This replaces `Spinner` wherever a whole screen — not a control inside one
 * — is waiting on its first data: a session resolving, a Lesson or a Ouvido
 * screen loading. `Spinner` remains the right choice inside a component that
 * has other content around it (`AudioControl`'s `loading` state, a button's
 * pending state), where an instrument-sized illustration would outweigh what
 * it sits beside.
 *
 * `standalone` mirrors `OfflineState`: the boundaries that render before
 * `AppLayout` mounts (signing in, resolving a session or a permission) carry
 * their own atmosphere; every other caller already sits inside `AppLayout`,
 * or inside Practice's own ground, and takes neither.
 */

interface LoadingStateProps {
  /** What is being waited on, in the learner's own words. Read aloud once. */
  label: string;
  standalone?: boolean;
}

export function LoadingState({ label, standalone = false }: LoadingStateProps) {
  return (
    <div className={styles.root} data-standalone={standalone || undefined}>
      {standalone ? <AmbientField count={2} anchor="viewport" /> : null}

      <div className={styles.stage} aria-hidden="true">
        <svg className={styles.note} data-tone="success" viewBox="0 0 20 24" width="16" height="19" style={{ left: '32px', top: '58px', animationDelay: '0s' }} aria-hidden="true">
          <ellipse cx="6.5" cy="18.5" rx="5.2" ry="4.2" transform="rotate(-20 6.5 18.5)" />
          <rect x="10.2" y="3" width="1.8" height="16" rx="0.9" />
          <path d="M12 3 C16.4 3.6 17.6 6.4 12 8.4 Z" />
        </svg>
        <svg className={styles.note} data-tone="accent" viewBox="0 0 20 24" width="13" height="16" style={{ right: '30px', top: '104px', animationDelay: '2.1s' }} aria-hidden="true">
          <ellipse cx="6.5" cy="18.5" rx="5.2" ry="4.2" transform="rotate(-20 6.5 18.5)" />
          <rect x="10.2" y="3" width="1.8" height="16" rx="0.9" />
        </svg>
        <svg className={styles.note} data-tone="warning" viewBox="0 0 20 24" width="14" height="17" style={{ left: '48px', top: '176px', animationDelay: '4.2s' }} aria-hidden="true">
          <ellipse cx="6.5" cy="18.5" rx="5.2" ry="4.2" transform="rotate(-20 6.5 18.5)" />
          <rect x="10.2" y="3" width="1.8" height="16" rx="0.9" />
          <path d="M12 3 C16.4 3.6 17.6 6.4 12 8.4 Z" />
        </svg>

        <svg className={styles.svg} viewBox="0 0 260 460" aria-hidden="true">
          <ellipse className={styles.shadow} cx="130" cy="448" rx="70" ry="9" />

          <g className={styles.lift}>
            <rect x="94" y="14" width="72" height="48" rx="16" className={styles.bodyFill} />
            <line x1="94" y1="28" x2="84" y2="28" className={styles.inkLine} />
            <circle cx="84" cy="28" r="6" className={styles.inkFill} />
            <line x1="94" y1="48" x2="84" y2="48" className={styles.inkLine} />
            <circle cx="84" cy="48" r="6" className={styles.inkFill} />
            <line x1="166" y1="28" x2="176" y2="28" className={styles.inkLine} />
            <circle cx="176" cy="28" r="6" className={styles.inkFill} />
            <line x1="166" y1="48" x2="176" y2="48" className={styles.inkLine} />
            <circle cx="176" cy="48" r="6" className={styles.inkFill} />

            <line x1="106" y1="62" x2="154" y2="62" className={styles.nut} />
            <rect x="105" y="62" width="50" height="155" rx="8" className={styles.bodyFill} />
            <line x1="109" y1="95" x2="151" y2="95" className={styles.fret} />
            <line x1="109" y1="122" x2="151" y2="122" className={styles.fret} />
            <line x1="109" y1="149" x2="151" y2="149" className={styles.fret} />
            <line x1="109" y1="176" x2="151" y2="176" className={styles.fret} />
            <line x1="109" y1="203" x2="151" y2="203" className={styles.fret} />

            {[121, 130, 139].map((x, index) => (
              <line
                key={x}
                x1={x}
                y1="65"
                x2={x}
                y2="214"
                strokeWidth="1.5"
                className={styles.string}
                style={{ animationDelay: `${index * 0.3}s` }}
              />
            ))}

            <ellipse cx="130" cy="325" rx="92" ry="108" className={styles.bodyFill} />
            <circle cx="130" cy="262" r="32" className={styles.soundholeBg} />
            <circle cx="130" cy="262" r="24" className={styles.soundholeRing} />
            <circle className={styles.ring} cx="130" cy="262" r="24" />
            <circle
              className={styles.ring}
              cx="130"
              cy="262"
              r="24"
              style={{ animationDelay: '0.55s' }}
            />
            <rect x="110" y="338" width="40" height="10" rx="4" className={styles.inkFill} />
          </g>
        </svg>
      </div>

      <p role="status" aria-live="polite">
        <Text as="span" variant="body-sm" tone="muted">
          {label}
        </Text>
      </p>
    </div>
  );
}
